import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { PieChart as PieChartIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const PerformanceAttribution = ({ sectors }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [viewMode, setViewMode] = React.useState('top');

  if (!sectors || sectors.length === 0) {
    return null;
  }

  // Helper function to get sector name based on current language
  const getSectorName = (sector) => {
    if (currentLang === 'vi' && sector.sector_name_vi) {
      return sector.sector_name_vi;
    }
    return sector.sector_name_en || sector.sector_name || sector.valuation_sector;
  };

  const handleViewChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Get top 5 and bottom 5 sectors
  const sortedSectors = [...sectors].sort((a, b) => b.final_score - a.final_score);
  const topSectors = sortedSectors.slice(0, 5);
  const bottomSectors = sortedSectors.slice(-5).reverse();
  
  const displaySectors = viewMode === 'top' ? topSectors : bottomSectors;

  // Prepare attribution data
  const attributionData = displaySectors.map(sector => ({
    name: getSectorName(sector),
    'Pillar A': parseFloat(sector.pillar_a_score || 0),
    'Pillar B': parseFloat(sector.pillar_b_score || 0),
    'Pillar C': parseFloat(sector.pillar_c_score || 0),
    'Pillar D': parseFloat(sector.pillar_d_score || 0),
    'Pillar E': parseFloat(sector.pillar_e_score || 0),
    'Pillar F': parseFloat(sector.pillar_f_score || 0),
    'Pillar G': parseFloat(sector.pillar_g_score || 0),
    'Pillar H': parseFloat(sector.pillar_h_score || 0),
    'Valuation': parseFloat(sector.val_adjustment || 0),
    finalScore: parseFloat(sector.final_score || 0),
  }));

  const pillarColors = {
    'Pillar A': '#11998e',
    'Pillar B': '#667eea',
    'Pillar C': '#f093fb',
    'Pillar D': '#4facfe',
    'Pillar E': '#43e97b',
    'Pillar F': '#fa709a',
    'Pillar G': '#feca57',
    'Pillar H': '#ff6b6b',
    'Valuation': '#9b59b6',
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      return (
        <Box
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            p: 2,
            borderRadius: 2,
            boxShadow: 3,
            border: '1px solid #e0e0e0',
            maxWidth: 300,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('dashboard.performanceAttribution.totalScore')}: {total.toFixed(1)}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontSize: '0.75rem' }}
            >
              {entry.name}: <strong>{entry.value.toFixed(1)}</strong>
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PieChartIcon sx={{ fontSize: 32, mr: 1, color: '#667eea' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('dashboard.performanceAttribution.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.performanceAttribution.subtitle', { 
                  type: viewMode === 'top' ? t('dashboard.performanceAttribution.top') : t('dashboard.performanceAttribution.bottom') 
                })}
              </Typography>
            </Box>
          </Box>
          
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            size="small"
          >
            <ToggleButton value="top">{t('dashboard.performanceAttribution.top5')}</ToggleButton>
            <ToggleButton value="bottom">{t('dashboard.performanceAttribution.bottom5')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <ResponsiveContainer width="100%" height={400} minWidth={400} minHeight={400}>
          <BarChart
            data={attributionData}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={100}
              stroke="#666"
              style={{ fontSize: '0.75rem' }}
            />
            <YAxis
              stroke="#666"
              label={{ value: t('dashboard.performanceAttribution.scoreContribution'), angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} animationDuration={50} animationEasing="ease-out" />
            <ReferenceLine y={0} stroke="#999" />
            
            {Object.keys(pillarColors).map(pillar => (
              <Bar
                key={pillar}
                dataKey={pillar}
                stackId="a"
                fill={pillarColors[pillar]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>

        {/* Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          {Object.keys(pillarColors).map(pillar => (
            <Box
              key={pillar}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: pillarColors[pillar],
                  borderRadius: 0.5,
                }}
              />
              <Typography variant="caption">{pillar}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PerformanceAttribution;

